import React, { useState } from 'react';
import Page from '../../../components/Page';
import { Icon, Breadcrumb, Table, Tab, Button, Modal, Header, Message } from 'semantic-ui-react';
import { Link, Prompt, Route, matchPath, NavLink } from 'react-router-dom';
import useFetch from 'use-http';
import { Formik } from 'formik';
import _ from 'lodash';
import MyMenu from '../menu';
import { TextInput, TableLine, BooleanInput } from '../inputs';
import AccountProgress from '../../../components/AccountProgress';
import { useTranslation } from 'react-i18next';

const fetch_config = { responseType: 'json', cachePolicy: 'no-cache' };

// Alt Ayarlar (Ürün Arama ve Detaylar)
function SubConfigs() {
    const { t } = useTranslation();
    return (
        <Table definition>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell width={2} />
                    <Table.HeaderCell>{t('value')}</Table.HeaderCell>
                </Table.Row>
            </Table.Header>

            <Table.Body>
                <TableLine title={t('productSearch')} description={t('productSearchDesc')}>
                    <BooleanInput name="_search" />
                </TableLine>
                <TableLine title={t('productDetails')} description={t('productDetailsDesc')}>
                    <BooleanInput name="_detail" />
                </TableLine>
            </Table.Body>
        </Table>
    );
}

// İkas İçin Özelleştirilmiş Genel Ayarlar
function GeneralConfigs() {
    const { t } = useTranslation();
    return (
        <Tab.Pane>
            <Table definition>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width={2} />
                        <Table.HeaderCell>{t('value')}</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    <TableLine title={t('urlLabel')}>
                        <TextInput name="public_url" placeholder="https://example.com/" />
                    </TableLine>

                    <TableLine title={t('shopName')}>
                        <TextInput name="storename" placeholder={t('shopName')} />
                    </TableLine>

                    
                    <TableLine title={t('idLabel')}>
                        <TextInput name="storeid" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                    </TableLine>

                    
                    <TableLine title={t('clientId')}>
                        <TextInput name="clientId" placeholder={t('clientId')} />
                    </TableLine>

                    
                    <TableLine title={t('clientSecret')}>
                        <TextInput name="clientSecret" placeholder={t('clientSecret')} />
                    </TableLine>
                </Table.Body>
            </Table>
            <SubConfigs />
            
            <Message info>
                <Icon name="info circle" />
                {t('ikasInfo')}
            </Message>
        </Tab.Pane>
    );
}

// Tab Yapılandırması
function Configs() {
    const { t } = useTranslation();
    const panes = [
        {
            menuItem: {
                as: NavLink,
                content: t('general'),
                to: '/settings/integrations/ikas',
                exact: true,
                key: 'ikas',
            },
            render: () => <Route path="/settings/integrations/ikas" exact component={GeneralConfigs} />,
        },
    ];
    
    const defaultActiveIndex = panes.findIndex((pane) => {
        return !!matchPath(window.location.pathname, {
            path: pane.menuItem.to,
            exact: true,
        });
    });

    return (
        <Page.Wrap>
            <Tab menu={{ secondary: true, pointing: true }} defaultActiveIndex={defaultActiveIndex} panes={panes} />
        </Page.Wrap>
    );
}

// Ana Bileşen
export default function ClientSettings() {
    const { t } = useTranslation();
    
    const { loading, response } = useFetch('/api/dahi/channel/get/ikas', fetch_config, []);
    const result = response && response.data;
    const [isShowing, setIsShowing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [showMessage, setShowMessage] = useState(true);

    
    const initialValues = React.useMemo(() => {
        if (!result) return;
        const { config } = (result && result.data) || {};
        return _.merge({ 
            public_url: '', 
            storename: '', 
            storeid: '', 
            clientId: '', 
            clientSecret: '',
            _search: false,
            _detail: false
        }, config);
    }, [result]);

    if (loading)
        return (
            <Page
                header={
                    <Breadcrumb size="big">
                        <Breadcrumb.Section as={Link} to={`/settings`}>
                            <Icon name="setting" />
                            {t('settings')}
                        </Breadcrumb.Section>
                        <Breadcrumb.Divider icon="right chevron" />

                        <Breadcrumb.Section as={Link} to={`/settings/integrations`}>
                            {t('integrations')}
                        </Breadcrumb.Section>
                        <Breadcrumb.Divider icon={{ name: 'asterisk', loading: true }} />
                    </Breadcrumb>
                }
                flex
            >
                
                <MyMenu activeItem="ikas">
                    <Page.Loader />
                </MyMenu>
            </Page>
        );

    function openModal() {
        setIsShowing(true);
    }
    function closeModal() {
        setIsShowing(false);
    }

    // Silme Fonksiyonu - Channel: ikas
    function removeIkas() {
        setDeleting(true);
        fetch('/api/dahi/channel/save', {
            method: 'POST',
            body: JSON.stringify({
                channel: 'ikas',
                data: { remove: true },
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => response.json())
            .then((json) => {
                if (json.success) {
                    setShowMessage(false);
                    setIsShowing(false);
                    setMessageType('positive');
                    setMessage(t('ikasAccountDeleted')); // MultiLang
                    setDeleting(false);
                    window.location.reload();
                } else {
                    setShowMessage(false);
                    setIsShowing(false);
                    setMessageType('negative');
                    setMessage(t('error') || 'Error'); // Fallback
                    setDeleting(false);
                }
            });
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => {
                if (values.client) values.client.name = values.name;

                // _host bilgisini public_url'den türetme mantığı
                try {
                    if (values.public_url) {
                        let urlObj = new URL(values.public_url);
                        values._host = urlObj.hostname.replace('www.', '');
                    }
                } catch (e) {
                    console.warn("Invalid URL format");
                }

                // Sabit URL'i ekle
                values.url = "https://api.myikas.com/api/v1/admin";

                fetch('/api/dahi/channel/save', {
                    method: 'POST',
                    body: JSON.stringify({
                        channel: 'ikas',
                        data: values,
                    }),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => response.json())
                    .then((json) => {
                        if (json.success) {
                            setShowMessage(false);
                            setMessageType('positive');
                            setMessage(t('ikasAccountConnected'));
                            values.active = true;
                            actions.resetForm({ values });
                        } else {
                            setShowMessage(false);
                            setMessageType('negative');
                            setMessage(t('error') || 'Error');
                            if (/"success":false/.test(json.message)) {
                                setMessage(t('ikasConnectionError'));
                            }
                            // Hata durumunda formu sıfırlamıyoruz ki kullanıcı düzeltebilsin
                            // actions.resetForm(values); 
                        }
                        actions.setSubmitting(false);
                    })
                    .catch((e) => {
                        console.error(e);
                        setShowMessage(false);
                        setMessageType('negative');
                        setMessage(t('error') || 'Error');
                        actions.setSubmitting(false);
                    });
            }}
        >
            {(props) => {
                return (
                    <Page
                        header={
                            <Breadcrumb size="big">
                                <Breadcrumb.Section as={Link} to={`/settings`}>
                                    <Icon name="setting" />
                                    {t('settings')}
                                </Breadcrumb.Section>
                                <Breadcrumb.Divider icon="right chevron" />

                                <Breadcrumb.Section as={Link} to={`/settings/integrations`}>
                                    {t('integrations')}
                                </Breadcrumb.Section>
                                <Breadcrumb.Divider icon="right chevron" />
                                <Breadcrumb.Section>Ikas {t('settings')}</Breadcrumb.Section>
                            </Breadcrumb>
                        }
                        extra={
                            <div className="ml-auto d-flex">
                                <AccountProgress></AccountProgress>
                                <Button disabled={!props.values.active} style={{ marginLeft: '10px' }} negative type="button" onClick={() => openModal()}>
                                    {t('remove')}
                                </Button>
                                <Button className="ml-auto" loading={props.isSubmitting} primary type="submit" onClick={props.submitForm}>
                                    {t('saveChanges')}
                                </Button>
                            </div>
                        }
                        flex
                    >
                        
                        <MyMenu activeItem="ikas">
                            <form onSubmit={props.handleSubmit} autoComplete="off">
                                <Prompt
                                    when={props.dirty}
                                    message={(location) =>
                                        location.pathname.startsWith('/settings/integrations/website-chat') ? true : t('questionWithoutSaving')
                                    }
                                />

                                <Configs />
                                <div style={{ textAlign: 'center' }}>
                                    <Message compact hidden={showMessage} positive={messageType === 'positive'} negative={messageType === 'negative'}>
                                        {message}
                                    </Message>
                                </div>
                            </form>
                        </MyMenu>
                        
                        {/* Delete Modal */}
                        <Modal open={isShowing} onClose={() => closeModal()} style={{ width: '500px' }}>
                            <Header icon="trash" content={t('removeTsoftConnection', { title: 'Ikas' })} />
                            <Modal.Content>
                                <p>{t('tsoftRemove', { title: 'Ikas' })}</p>
                            </Modal.Content>
                            <Modal.Actions>
                                <Button color="red" onClick={() => closeModal()}>
                                    <Icon name="remove" /> {t('no')}
                                </Button>
                                <Button color="green" loading={deleting} onClick={() => removeIkas(props.values)}>
                                    <Icon name="checkmark" /> {t('yes')}
                                </Button>
                            </Modal.Actions>
                        </Modal>
                    </Page>
                );
            }}
        </Formik>
    );
}